import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subject, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CollectionItemDirective } from '../../services/collections/collection-item.directive';
import { WizDatasource } from '../../services/collections/wiz-datasource';
import { WizDragDropContainer } from './../../directives/behavior/drag-drop/wiz-drag-drop.directive';
import {
  WizDraggableComponent,
  WizDraggableItem,
  WizDropControlFn,
  WizDropItemEvent,
} from './../../directives/behavior/drag-drop/wiz-drag-drop.model';
import { WizItemRendererDirective } from './../../interfaces/wiz-item-renderer';
import { WizTranslatableComponent } from './wiz-translatable-component';
import { WDSChanges, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEventManager } from '../../events/wiz-event.manager';
import { WizEvent } from '../../events/wiz-event';
import { VirtualScrollerComponent } from '../controls/wiz-list/virtual-scroller';

/** Classe de base pour les objets à collection (list / tree / grid ?) */
@Directive()
export abstract class WizBaseCollector<T = any>
  extends WizTranslatableComponent
  implements AfterViewInit, WizDraggableComponent, OnDestroy
{
  // le virtualScroll vu comme un container
  @ViewChild('wizCdkList', { read: CdkVirtualScrollViewport, static: true })
  public wizVirtualScroll: CdkVirtualScrollViewport;

  // le virtualScroll vu comme un container
  @ViewChild('wizCdkTileList', { read: VirtualScrollerComponent, static: true })
  public wizVirtualTileScroll: VirtualScrollerComponent;


  @ViewChild(WizDragDropContainer)
  dragDropContainer: WizDragDropContainer;

  @ViewChild('wizCdkList', { read: ElementRef, static: true })
  public wizVSElemRef: ElementRef;

  // le renderer de base (texte simple en span)
  @ViewChild('defaultRenderer', { read: TemplateRef, static: true })
  defaultRenderer: TemplateRef<any>;

  // le renderer dynamique pour affichage du WizRenderer
  @ViewChild('dynamicRenderer', { read: TemplateRef, static: true })
  dynamicRenderer: TemplateRef<any>;

  /**
   * Liste des items renderers
   */
  @ViewChildren(WizItemRendererDirective, { read: WizItemRendererDirective })
  _itemRenderers: QueryList<WizItemRendererDirective<any, T>>;

  // neutralisation de la sélection de texte lorsque le drag est actif
  // ou en cas de demande explicite
  @HostBinding('class.prevent-select-class') get selectTextCss() {
    return !this.selectableText; // || this.dragEnabled;
  }

  @Input() dragEnabled: boolean = false;
  @Input() dropEnabled: boolean = false;
  // Suppression de l'élément déplacé sur le drop
  @Input() dragMove: boolean = false;
  // Réordonnencement interne de la liste par drag/drop
  @Input() enableSelfReorder: boolean = false;
  // Affichage de l'indicateur de drop
  @Input() shopDropIndicator: boolean = false;
  @Input() dropControlFunction: WizDropControlFn;

  /** Constructor with translation engine
   * @param cdref: ChangeDetectorRef
   * @param elementRef: the dom of the component
   * @param renderer: object that will allow the interaction between the component and the DOM
   */
  constructor(cdref: ChangeDetectorRef, public elementRef: ElementRef, renderer: Renderer2, public ngZone: NgZone) {
    super(cdref, elementRef, renderer);

    // marquer comme NON Basic pour cycle de vie du model_update
    this.basicComponent = false;
  }

  // ####
  // ####
  // #### configuration ####
  // ####
  // ####

  /**
   * éventuelle fonction de transformation des items droppés. Va réaliser la transfo avant le dragAddItem
   * la signature est func(WizBaseCollector, object)
   */
  @Input() public dropTransformFunction: Function = null;

  // drag autorisé
  @Input() selectableText: boolean = false;

  /** Champ utilisé pour l'affichage dans l'input. */
  protected _wizLabelField: string;

  @Input()
  set wizLabelField(wizLabelField: string) {
    this._wizLabelField = wizLabelField;
  }

  public get wizLabelField(): string {
    return this._wizLabelField;
  }

  /** renderer custom pour les items */
  @Input() wizItemRenderer: any;

  /** définition du template à utiliser pour les rendus :  */
  public getTemplate() {
    return this.wizItemRenderer ? this.dynamicRenderer : this.defaultRenderer;
  }

  private _DEFAULT_ITEM_HEIGHT: number = 30;

  /**
   * the height of each row in the list
   */
  @Input() itemHeight: number = this._DEFAULT_ITEM_HEIGHT;

  get itemHeightStr(): string {
    return (this.itemHeight || this._DEFAULT_ITEM_HEIGHT) + 'px';
  }

  /**
   * S'assure que l'index ou l'élément donné en paramètre est visible
   *
   * @param pElement l'index vers lequel le viewport doit scroller
   */
  public ensureIndexIsVisible(pElement: number | T) {
    // coercion du paramètre
    const idx: number = !isNaN(pElement as any)
      ? (pElement as number)
      : this._wizDataProvider.getItemIndex(pElement as T);
    
    this.wizVirtualScroll?.scrollToIndex(idx, 'auto');
    this.wizVirtualTileScroll?.scrollToIndex(idx);
  }

  // ####
  // ####
  // #### collections & selections ####
  // ####
  // ####

  /**
   * Sets the data provider that will be rendered in the list
   */
  private _wizDataProvider: WizDatasource<T>;

  @Input()
  set wizDataProvider(data: WizDatasource<T>) {
    // c'est exactement la même DS et je n'ai pas été détruit, je ne vais pas plus loin
    /*if (this._wizDataProvider === data) {
      return;
    }*/

    this._datasourceRefChanged$.next(true);
    this._datasourceRefChanged$.complete();
    this._datasourceRefChanged$ = new Subject<boolean>();

    this._wizDataProvider = data;

    if (!!this._wizDataProvider) {
      this._wizDataProvider.changes.pipe(
          takeUntil(
            merge(
              this._destroyed$,
              this._datasourceRefChanged$
            )
          )
        ).subscribe((changes) => {
          this.onProviderChanges(changes);
          this.cdref.markForCheck();
          this.refreshProvider();
      });

      // patch. il est possible que le dernier changement soit un selectionChanged ou programmaticChanged
      // Dans ce cas, on check la taille du viewPort
      if (
        this.wizVirtualScroll &&
        !!this._wizDataProvider.lastChange &&
        (this._wizDataProvider.lastChange.type === 'selectionChanged' ||
          this._wizDataProvider.lastChange.type === 'programmaticChanged')
      ) {
        this.wizVirtualScroll.checkViewportSize();
      }
    }
    this.refreshProvider();
  }

  public get wizDataProvider(): WizDatasource<T> {
    return this._wizDataProvider;
  }

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####

  /**
   * Méthode déclenchée sur changement dans le wizDataProvider.
   *
   * Par défaut, cette méthode déclenche le rafraichissement de la vue.
   * Elle peut être overridée dans les sous-composants pour ajouter des comportements sur changements dans la DS
   */
  protected onProviderChanges(changes: WDSChanges): void {
    // ATTENTION : c'est l'item en cours de toggle, pas la sélection active
    if (changes.type === 'selectionChanged') {
      WizEventManager.dispatchEvent(
        this,
        new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGED, this, this._wizDataProvider.selectedItems)
      );
      if (this.manageModel) {
        // dispatch du changement de modèle
        WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, this, this._wizValue));
      }
    }

    if (
      this.wizVirtualScroll &&
      changes.type !== 'selectionChanged' &&
      changes.type !== 'programmaticChanged' &&
      changes.type !== 'nodeChanged'
    ) {
      this.wizVirtualScroll.checkViewportSize();
    }

    if (changes.type === 'itemUpdated') {
      const updatedItems: T[] = changes.value;

      if (!!updatedItems && updatedItems.length) {
        updatedItems.forEach((item) => {
          if (this._renderersMap && this._renderersMap.has(item)) {
            const renderer: WizItemRendererDirective<any, T> = this._renderersMap.get(item);
            renderer.data = item;
          }
        });
      }
    }

    this.cdref.markForCheck();
  }

  /** accesseur sur le libellé d'un item sélectionné */
  public getItemLabel(item: any): string {
    if (!item) {
      return '';
    }
    return this.wizLabelField ? item[this.wizLabelField] : item;
  }

  /**
   * Sujet de composant détruit
   */
  private _destroyed$: Subject<boolean> = new Subject<boolean>();

  /**
   * Sujet de référence datasource détruite
   */
  private _datasourceRefChanged$: Subject<boolean> = new Subject<boolean>();

  /** On destroy, désenregistrement éventuel des targets du dragManager */
  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
    this._datasourceRefChanged$.complete();
    super.ngOnDestroy();
  }

  /** à overrider pour alimentation de la collection de travail */
  public refreshProvider(): void {}

  /** mouseDown sur un item de la collection => check pour start du drag */
  public onInitDrag(pItem: CollectionItemDirective): void {
    this.dragDropContainer?.startDrag({
      target: pItem.elementRef.nativeElement,
      data: pItem.item,
    });
  }

  /** Check sur le drag over, à surcharger au besoin pour interdire le drop. */
  checkDragOver(_: WizDropItemEvent): boolean {
    return true;
  }

  /**
   * Ajout d'un item depuis un drag'n'drop
   */
  public onAddItemAfterDrag({ item, targetPosition }: WizDropItemEvent): void {
    const itemToAdd: any = this.dropTransformFunction ? this.dropTransformFunction(this, item.data) : item.data;
    const realTargetPosition = targetPosition + (this.wizVirtualScroll?.getRenderedRange()?.start ?? 0);
    this._wizDataProvider.addItem(itemToAdd, realTargetPosition);
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.DRAG_DROPPED, this, itemToAdd));
  }

  // self Drag'n'drop. Changement de place dans la collection (le array source)
  // attention, pas d'impact en cas de sort...
  public onMoveItemAfterDrag({item, targetPosition}: WizDropItemEvent): void {
    // il faut prendre en compte le fait que l'on soit en virtualScroll 
    const realTargetPosition = targetPosition + (this.wizVirtualScroll?.getRenderedRange()?.start ?? 0);

    const currentIndex: number = this.wizDataProvider.getItemIndex(item.data as T);
    this.wizDataProvider.moveItem(
      currentIndex,
      realTargetPosition > currentIndex && realTargetPosition !== this.wizDataProvider.length - 1
        ? realTargetPosition - 1
        : realTargetPosition
    );
    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.DRAG_MOVED, this, item));
  }

  /** suppression de l'item de la collection d'origine sur move (target # origin)
   * @param item, contrat d'interface CollectionItemDirective. item.item pour l'objet "métier"
   */
  public onRemoveItemAfterDrag({ data }: WizDraggableItem): void {
    this.wizDataProvider.removeItem(data as T);
    WizEventManager.dispatchEvent(
      this.elementRef,
      new WizEvent(WIZ_EVENT_TYPE.DROP_LOST_ITEM, this, data)
    );
  }

  // ####
  // ####
  // #### gestion de la taille du virtualScroll ####
  // #### (fix affichage partiel sur changement de vue)
  // ####

  /** gestion du resize initial (sinon, manque un morceau en entrée de vue DEPUIS une autre vue) */
  ngAfterViewInit() {
    super.ngAfterViewInit();

    WizEventManager.addListener(this, this, WIZ_EVENT_TYPE.SELECTION_CHANGING, this.onSelectionChanging);

    if (this._itemRenderers) {
      if (this._itemRenderers.length) {
        this.handleRenderers(this._itemRenderers.toArray());
      }

      this._itemRenderers.changes
        .pipe(takeUntil(this._destroyed$))
        .subscribe((_) => this.handleRenderers(this._itemRenderers.toArray()));
    }
  }

  private _renderersMap: Map<T, WizItemRendererDirective<any, T>>;

  private handleRenderers(pRenderers: WizItemRendererDirective<any, T>[]): void {
    if (!this._renderersMap) {
      this._renderersMap = new Map();
    }

    pRenderers.forEach((renderer) => this._renderersMap.set(renderer.data, renderer));
  }

  /**
   * Ecouteur de changement de sélection en cours. Cette évenement est provoqué par l'humain
   */
  protected onSelectionChanging(pEvent: WizEvent): void {
    // si l'événement n'a pas était prevent, on valide la sélection
    if (!pEvent.defaultPrevented) {
      // on utilise le toggle plutôt que le select, car il s'agit d'une action provoquée par l'humain
      this.wizDataProvider.toggle(pEvent.value);
    }
  }

  /**
   * Lance le processus de changement de sélection.
   *
   * Ici, on va émettre un évènement indiquant qu'un changement de sélection est en cours.
   *
   * Cela laisse la possibilité d'éventuellement interrompre la sélection en utilisant le mode "capture"
   *
   * @param value La valeur à sélectionner
   */
  protected doSelect(value: any) {
    // composant désactivé, on ne réagit pas
    if (this.wizDisabled) {
      return;
    }
    WizEventManager.dispatchEvent(
      !!this.wizVSElemRef ? this.wizVSElemRef : this,
      new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGING, this, value)
    );
  }

  /**
   * on va émettre un évènement indiquant qu'un double click est en cours.
   *
   * Cela laisse la possibilité d'éventuellement interrompre la sélection en utilisant le mode "capture"
   *
   * @param value La valeur à sélectionner
   */
  protected emitDoubleClick(value: any): void {
    // composant désactivé, on ne réagit pas
    if (this.wizDisabled) {
      return;
    }

    WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.DOUBLE_CLICK, this, value));
  }

  /** sur fin de scroll par clavier, refresh visuel (pour marque de focus) */
  public refreshAfterScroll(pEvent): void {
    this.cdref.markForCheck();
  }
}
